import { Controller } from "@hotwired/stimulus";

// Diff between draft (current drawer inputs) and applied baseline
// (snapshot at drawer-open or last rebaseline). Enables/disables Apply.
//
// Public API:
//   serializeDraft()           — current draft as URL-param hash.
//   rebaseline()               — new baseline = current draft.
//   applyValues(paramsHash)    — REPLACE all inputs with paramsHash (absent
//                                keys are CLEARED, not left alone) + recompute.
//   isDirty()                  — order-insensitive, blank-tolerant draft-vs-
//                                baseline comparison (true ⇒ unapplied edits).
//
// filter-diff:values-applied event contract (consumed by the sibling
// pill-group / chip-input controllers):
//   applyValues() ends by dispatching a bubbling CustomEvent
//     "filter-diff:values-applied"  with  detail: { values: <the full hash> }
//   Consumers read event.detail.values[bareKey]:
//     • Array  → the multi-value selection for that key (e.g. ["US","CA"])
//     • scalar → the single value (string)
//     • undefined (key absent) → CLEAR that control
//   applyValues() itself sets <select multiple>, checkbox/radio, and scalar
//   <input>/<select> directly (and clears those whose key is absent). It does
//   NOT enumerate the DYNAMIC hidden inputs (name="key[]") that pill-group /
//   chip-input render — those controllers MUST rebuild their own hidden inputs
//   from event.detail.values on each dispatch (rebuild-from-detail, so an
//   absent key naturally clears them).
export default class extends Controller {
  static targets = ["applyBtn"];
  static values = { applied: { type: Object, default: {} } };

  connect() {
    this._baseline = this.serializeDraft();
    // Coalesce mutation bursts and input/change events into a single
    // per-frame recompute. Select2 mutates ~250 option classes per
    // keystroke; without coalescing, each mutation fires a full
    // serializeDraft walk and the main thread thrashes for tens of
    // seconds when the user types into a country/state/CFV dropdown.
    this._pendingFrame = null;
    this._onChange = () => {
      if (this._pendingFrame != null) return;
      this._pendingFrame = requestAnimationFrame(() => {
        this._pendingFrame = null;
        this._recomputeDiff();
      });
    };
    this.element.addEventListener("input", this._onChange);
    this.element.addEventListener("change", this._onChange);
    this._classObserver = new MutationObserver(this._onChange);
    this._classObserver.observe(this.element, {
      subtree: true, attributes: true,
      attributeFilter: ["class", "aria-pressed"]
    });
    this._recomputeDiff();
  }

  disconnect() {
    this.element.removeEventListener("input", this._onChange);
    this.element.removeEventListener("change", this._onChange);
    this._classObserver?.disconnect();
    if (this._pendingFrame != null) {
      cancelAnimationFrame(this._pendingFrame);
      this._pendingFrame = null;
    }
  }

  rebaseline() {
    this._baseline = this.serializeDraft();
    this._recomputeDiff();
  }

  // BUG #10: true when the draft differs from the baseline. Reuses the SAME
  // order-insensitive, blank-tolerant comparison (_equalDraft) and the SAME
  // _baseline that drive _recomputeDiff, so a pristine drawer is never "dirty"
  // even though serializeDraft is verbose (empty [] per multi-select, "" per
  // empty scalar) while the baseline may have been captured the same way.
  // filter_drawer._onHidden uses this to decide whether to stash an unapplied
  // draft — fixing the spurious "restore unsaved edits?" notice on every open.
  isDirty() {
    return !this._equalDraft(this.serializeDraft(), this._baseline);
  }

  serializeDraft() {
    const out = {};
    this.element.querySelectorAll("[name]").forEach((el) => {
      const raw = el.name;
      if (!raw) return;
      if (el.disabled) return;
      if (el.type === "checkbox" && !el.checked) return;
      if (this._isRangeAtDefaultBound(el)) return;
      if (raw.endsWith("[]")) {
        const bare = raw.slice(0, -2);
        (out[bare] ||= []).push(el.value);
      } else {
        out[raw] = el.value;
      }
    });
    // Handle <select multiple> directly — collect selected options under bare key
    this.element.querySelectorAll("select[multiple]").forEach((sel) => {
      const raw = sel.name;
      if (!raw || !raw.endsWith("[]")) return;
      const bare = raw.slice(0, -2);
      out[bare] = Array.from(sel.selectedOptions).map((o) => o.value);
    });
    return out;
  }

  // LRPO-5507 (item 21) — an <input type="range"> ALWAYS carries a value, so the
  // quality sliders emitted quality_min:"0" / quality_max:"100" on every
  // serialize. _buildUrl writes any non-empty value ("0" is not ""), so EVERY
  // Apply appended &quality_min=0&quality_max=100 — two keys that
  // apply_quality_filter short-circuits to a no-op and that produce no chip, yet
  // inflated the drawer's applied-KEY count by +2 over the chip count. That delta
  // IS the counter mismatch QA reported, and the same pollution was written into
  // every saved preset.
  //
  // A slider resting AT its bound carries no information: the server defaults a
  // missing quality_min to 0 and a missing quality_max to 100, so dropping it is
  // lossless — a min of 0 alongside a max of 80 still serializes the max.
  // Keyed off the filter-range target (the explicit contract with
  // filter_range_controller), not a name convention.
  _isRangeAtDefaultBound(el) {
    if (el.type !== "range") return false;
    const target = el.getAttribute("data-filter-range-target");
    if (target === "rangeMin") return el.min !== "" && el.value === el.min;
    if (target === "rangeMax") return el.max !== "" && el.value === el.max;
    return false;
  }

  applyValues(paramsHash) {
    const hash = paramsHash || {};
    // BUG #5: REPLACE, not MERGE. The baseline handed in (Reset / preset-load /
    // reloadDraft) is compact_blank-stripped, so any input whose bare key is
    // ABSENT from the hash must be CLEARED — otherwise stale selections survive.
    // We therefore iterate EVERY [name] input and either set it from the hash
    // (key present) or clear it (key absent).
    this.element.querySelectorAll("[name]").forEach((el) => {
      const raw = el.name;
      if (!raw) return;
      const bare = raw.endsWith("[]") ? raw.slice(0, -2) : raw;
      // BUG #11: pill-group / chip-input OWN their dynamic name="key[]" hidden
      // inputs and rebuild them from filter-diff:values-applied (detail.values,
      // absent key ⇒ clear). Don't let the generic else-branch stringify an
      // Array into their .value (CSV poison: quality_band[].value = "warm,cold").
      // Scoped to HIDDEN array inputs only — <select multiple> (handled by the
      // SELECT branch) and scalar hidden inputs (name without []) are untouched.
      if (el.type === "hidden" && raw.endsWith("[]")) return;
      // MEDIUM regression guard: skip control inputs that encode UI MODE rather
      // than a persisted filter, and the dual-named rep <select>. These are
      // never in the applied/compacted hash, so the generic clear/set would
      // desync them (e.g. force-uncheck `_include_via_territory` while the rep
      // select still posts rep_id[]). filter_drawer listens for the
      // filter-diff:values-applied event and re-syncs the rep group
      // (checkbox + select name + selectedness) COHERENTLY from detail.values.
      if (this._isRepGroupInput(bare)) return;
      // HIGH regression: <input type="range"> (quality_min / quality_max) — the
      // generic clear-branch `el.value = "" ` snaps a range slider to its
      // MIDPOINT (50), so Reset/preset-load (no quality key in the hash) used to
      // drag the quality range to a degenerate 50/50. filter_range OWNS these
      // inputs and restores them from the filter-diff:values-applied event below
      // (same rebuild-from-detail pattern as pill-group / chip-input, BUG #11).
      // Skip them here so we never write to their .value.
      if (el.type === "range") return;
      const present = Object.prototype.hasOwnProperty.call(hash, bare);
      const value = hash[bare];

      if (el.tagName === "SELECT" && el.multiple) {
        // Multi-select: select the options the hash names; deselect everything
        // when the key is absent (CLEAR) or the value is not an array.
        const wanted = present && Array.isArray(value) ? value : [];
        Array.from(el.options).forEach((opt) => {
          opt.selected = wanted.includes(opt.value);
        });
        // Repaint Select2 (its pills don't follow opt.selected on their own) on
        // BOTH set and clear. We use "change.select2" (NOT a bare "change")
        // deliberately: filter_select2's bridge listens on the BASE "change"
        // type, so the namespaced refresh repaints the pills WITHOUT re-firing
        // the full change cascade (and without tripping the filter_select2
        // re-entrancy guard). Feature-guarded so it's a no-op for plain inputs
        // and in jsdom tests with no jQuery/Select2. (Prior fix c36c8af14.)
        if (window.$) {
          const $el = window.$(el);
          if ($el.data && $el.data("select2")) $el.trigger("change.select2");
        }
      } else if (el.type === "checkbox" || el.type === "radio") {
        // BUG #9: checkbox/radio branch. serializeDraft skips unchecked
        // checkboxes and emits el.value when checked, so a restored hash holds
        // a truthy value (e.g. "on", or that value inside an array) for a
        // checked control. Check when the hash carries this control's value;
        // uncheck when the key is absent or the value doesn't match.
        el.checked = present && this._checkboxValueMatches(el, value);
      } else {
        // Scalar <input> / single <select>: set when present, clear when absent.
        el.value = present && value != null ? value : "";
      }
    });
    // CONTRACT (BUG #5/#11): carry the FULL hash so pill-group / chip-input
    // controllers can rebuild their dynamic hidden inputs from detail.values
    // (absent key ⇒ clear). See the class-level contract comment above.
    this.element.dispatchEvent(new CustomEvent("filter-diff:values-applied", {
      detail: { values: hash },
      bubbles: true
    }));
    this._onChange();
  }

  // Does this checkbox/radio's value appear in the hash value for its key?
  // Mirrors serializeDraft, which emits el.value when checked. The hash value
  // may be a scalar (single key) or an array (key[] multi). For a checkbox with
  // no explicit value attribute el.value is "on"; any truthy stored value for
  // the bare key counts as "checked".
  _checkboxValueMatches(el, value) {
    if (Array.isArray(value)) return value.map(String).includes(el.value);
    if (value == null || value === "" || value === false) return false;
    if (el.type === "radio") return String(value) === el.value;
    return true; // checkbox: any truthy stored value ⇒ checked
  }

  // Inputs the generic clear/set must leave alone (filter_drawer re-syncs them
  // coherently from the values-applied event): the `_`-prefixed UI-only control
  // (_include_via_territory) and the dual-named rep <select> (rep_id /
  // assigned_rep_id) whose name encodes mode, not just selection.
  _isRepGroupInput(bareKey) {
    return bareKey.startsWith("_") ||
           bareKey === "rep_id" ||
           bareKey === "assigned_rep_id";
  }

  // LRPO-5507 (item 19) — Apply is NEVER disabled. It used to be gated on the
  // draft differing from the baseline, and filter_drawer#apply rebaselines 1500ms
  // after a successful Apply — so the button disabled itself the moment it
  // succeeded. Re-running the SAME filter to pick up newly-scanned leads (the
  // whole point of a live lead list) then required dirtying an input and undoing
  // the change. Applying an unchanged filter set is a legitimate, idempotent
  // request; the diff stays intact for isDirty() (filter_drawer._onHidden's
  // unsaved-edits stash) and for the "Applied ✓" affordance.
  _recomputeDiff() {
    if (!this.hasApplyBtnTarget) return;
    this.applyBtnTarget.disabled = false;
    if (!this.applyBtnTarget.classList.contains("is-applied")) {
      this.applyBtnTarget.textContent = "Apply filters";
    }
  }

  _equalDraft(a, b) {
    const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
    for (const k of keys) {
      const va = a?.[k], vb = b?.[k];
      if (Array.isArray(va) || Array.isArray(vb)) {
        const sa = new Set(Array.isArray(va) ? va : (va == null ? [] : [va]));
        const sb = new Set(Array.isArray(vb) ? vb : (vb == null ? [] : [vb]));
        if (sa.size !== sb.size) return false;
        for (const x of sa) if (!sb.has(x)) return false;
      } else if ((va ?? "") !== (vb ?? "")) {
        return false;
      }
    }
    return true;
  }
}
